import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../lib/env';
import type { CompleteVideoResponse, Workspace } from '../lib/types';
import { fixWebmDuration } from '../lib/webm-duration';
import {
  AUDIO_ONLY_BITRATE,
  SCREEN_AUDIO_BITRATE,
  detectSupport,
  pickCodec,
  type CaptureSupport,
  type CodecChoice,
  type RecordingMode,
} from './codec';
import {
  Compositor,
  DEFAULT_COMPOSITOR_SETTINGS,
  mixAudio,
  type BubbleCorner,
  type CompositorSettings,
  type MixedAudio,
} from './composer';
import { CaptureDenied, requestCamera, requestDisplay, requestMic, stopStream } from './media';
import { StreamingUpload, type UploadState, type UploadTransport } from './uploader';

/**
 * The recorder state machine.
 *
 *   idle ──arm()──▶ ready ──start()──▶ countdown ──▶ recording ⇄ paused
 *                     ▲                                  │
 *                     └────────restart()─────────────────┤
 *                                                        ▼
 *                                                    finishing ──▶ preview
 *
 * The rule that everything else bends around: the source MediaStream is
 * acquired once, in `arm()`, and is not released until the user leaves the
 * recorder. Restart tears down the MediaRecorder and the in-flight upload and
 * builds new ones over the *same* stream, so the browser never asks "which
 * screen would you like to share?" twice for one recording session. That is
 * the specific thing Cap got wrong.
 *
 * Two modes run through this one machine:
 *
 *   screen — getDisplayMedia, optional mic, optional webcam bubble. The mic is
 *            a garnish; a silent walkthrough is a perfectly good recording.
 *   audio  — getUserMedia only, no display prompt, no compositor. The mic is
 *            the *whole* source, so being denied it is a hard failure rather
 *            than a degradation, and `arm()` is where that is discovered.
 *
 * Everything after `arm()` — countdown, pause, restart, upload-while-recording,
 * the finish/preview handoff — is deliberately shared. The only divergences
 * are which stream `arm()` acquires, what `buildComposed()` assembles, and the
 * bitrate handed to MediaRecorder.
 */

export type RecorderPhase =
  | 'unsupported'
  | 'idle'
  | 'arming'
  | 'ready'
  | 'countdown'
  | 'recording'
  | 'paused'
  | 'finishing'
  | 'preview';

/** Chunk cadence. Small enough to keep parts flowing, large enough to be cheap. */
const TIMESLICE_MS = 3000;
const COUNTDOWN_FROM = 3;
/** Past this we nudge; we never hard-stop somebody mid-sentence. */
export const LONG_RECORDING_MS = 55 * 60 * 1000;

export interface UseRecorderOptions {
  transport: UploadTransport;
  /** Screen capture, or a microphone-only voice note. Defaults to screen. */
  mode?: RecordingMode;
  /** Where the recording lands. Ignored for request-link uploads. */
  workspace?: Workspace;
  orgId?: string | null;
  /** Title used at creation time; the preview screen can change it. */
  makeTitle: () => string;
  /** Skip the 3-2-1. */
  countdown?: boolean;
}

export interface RecorderApi {
  phase: RecorderPhase;
  mode: RecordingMode;
  support: CaptureSupport;
  codec: CodecChoice | null;
  error: string | null;
  /** Milliseconds of recorded material, excluding paused time. */
  elapsedMs: number;
  countdown: number;
  upload: UploadState;
  result: CompleteVideoResponse | null;
  shareId: string | null;

  /** Video-only stream for the on-screen preview. Never contains audio. */
  monitor: MediaStream | null;
  cameraMonitor: MediaStream | null;
  /** The live microphone, for the input-level meter. Null when there is none. */
  micMonitor: MediaStream | null;
  hasDisplay: boolean;
  hasMic: boolean;
  hasCamera: boolean;
  /** True once the mode's required source is in hand and recording can start. */
  armed: boolean;
  micMuted: boolean;
  bubble: CompositorSettings;

  /** Local playback of the finished take, duration header patched. */
  previewUrl: string | null;
  previewBlob: Blob | null;

  arm: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  setMicMuted: (muted: boolean) => void;
  setBubble: (patch: Partial<CompositorSettings>) => void;
  moveBubble: (corner: BubbleCorner) => void;

  start: () => void;
  skipCountdown: () => void;
  pause: () => void;
  resume: () => void;
  togglePause: () => void;
  restart: () => void;
  stop: () => Promise<void>;
  discard: () => Promise<void>;
  retryUpload: () => Promise<void>;
  reset: () => void;
  releaseSources: () => void;
}

function stopRecorder(recorder: MediaRecorder): Promise<void> {
  return new Promise((resolve) => {
    if (recorder.state === 'inactive') {
      resolve();
      return;
    }
    recorder.addEventListener('stop', () => resolve(), { once: true });
    try {
      recorder.stop();
    } catch {
      resolve();
    }
  });
}

export function useRecorder(options: UseRecorderOptions): RecorderApi {
  const mode: RecordingMode = options.mode ?? 'screen';
  const support = useMemo(() => detectSupport(), []);
  const codec = useMemo(
    () => (support.mediaRecorder ? pickCodec(mode) : null),
    [mode, support.mediaRecorder],
  );
  const modeSupported = mode === 'audio' ? support.audioOk : support.screenOk;

  const [phase, setPhase] = useState<RecorderPhase>(support.ok ? 'idle' : 'unsupported');
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [upload, setUpload] = useState<UploadState>({
    phase: 'idle',
    videoId: null,
    shareId: null,
    uploadedBytes: 0,
    capturedBytes: 0,
    partsUploaded: 0,
    partsInFlight: 0,
    error: null,
  });
  const [result, setResult] = useState<CompleteVideoResponse | null>(null);
  const [monitor, setMonitor] = useState<MediaStream | null>(null);
  const [cameraMonitor, setCameraMonitor] = useState<MediaStream | null>(null);
  const [micMonitor, setMicMonitor] = useState<MediaStream | null>(null);
  const [hasDisplay, setHasDisplay] = useState(false);
  const [hasMic, setHasMic] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [micMuted, setMicMutedState] = useState(false);
  const [bubble, setBubbleState] = useState<CompositorSettings>(DEFAULT_COMPOSITOR_SETTINGS);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const displayRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<MediaStream | null>(null);
  const compositorRef = useRef<Compositor | null>(null);
  const audioRef = useRef<MixedAudio | null>(null);
  const composedRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const uploaderRef = useRef<StreamingUpload | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const countdownTimerRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const accumulatedRef = useRef(0);
  const segmentStartRef = useRef(0);
  const elapsedRef = useRef(0);
  const bubbleRef = useRef<CompositorSettings>(DEFAULT_COMPOSITOR_SETTINGS);
  const previewUrlRef = useRef<string | null>(null);
  /** Always the mode being rendered; read by callbacks that outlive a render. */
  const modeRef = useRef<RecordingMode>(mode);
  modeRef.current = mode;
  /** The mode the sources currently in hand were acquired for. */
  const armedModeRef = useRef<RecordingMode>(mode);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /** Lets `arm` and the MediaRecorder error handler reach the current `stop`. */
  const stopRef = useRef<() => Promise<void>>(async () => {});

  // ---- clock ---------------------------------------------------------------

  const stopTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startTick = useCallback(() => {
    stopTick();
    segmentStartRef.current = performance.now();
    tickRef.current = window.setInterval(() => {
      const value = accumulatedRef.current + (performance.now() - segmentStartRef.current);
      elapsedRef.current = value;
      setElapsedMs(value);
    }, 200);
  }, [stopTick]);

  const settleTick = useCallback(() => {
    if (segmentStartRef.current > 0) {
      accumulatedRef.current += performance.now() - segmentStartRef.current;
      segmentStartRef.current = 0;
    }
    elapsedRef.current = accumulatedRef.current;
    setElapsedMs(accumulatedRef.current);
    stopTick();
  }, [stopTick]);

  /** Revoking an object URL is a side effect, so it never runs in a setState. */
  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
    setPreviewBlob(null);
  }, []);

  const resetClock = useCallback(() => {
    stopTick();
    accumulatedRef.current = 0;
    segmentStartRef.current = 0;
    elapsedRef.current = 0;
    setElapsedMs(0);
  }, [stopTick]);

  // ---- pipeline ------------------------------------------------------------

  /**
   * (Re)build the stream MediaRecorder will consume. Safe to call whenever a
   * source is added or removed while *not* recording; never called during a
   * recording, because MediaRecorder cannot change its input mid-flight.
   */
  const buildComposed = useCallback((): MediaStream | null => {
    // Audio-only is the degenerate case of the same pipeline: one source, one
    // track, no mixing and no canvas. Handing the mic track straight to
    // MediaRecorder keeps the graph out of the way entirely.
    if (modeRef.current === 'audio') {
      audioRef.current?.close();
      audioRef.current = null;
      const track = micRef.current?.getAudioTracks()[0];
      if (!track) return null;
      const composed = new MediaStream([track]);
      composedRef.current = composed;
      // There is nothing to look at; the stage renders a level meter instead.
      setMonitor(null);
      return composed;
    }

    const display = displayRef.current;
    if (!display) return null;

    audioRef.current?.close();
    const audio = mixAudio([display, micRef.current]);
    audioRef.current = audio;

    let videoTrack: MediaStreamTrack | undefined;

    if (cameraRef.current) {
      if (compositorRef.current) {
        compositorRef.current.setCamera(cameraRef.current);
      } else {
        compositorRef.current = new Compositor(display, cameraRef.current);
        compositorRef.current.update(bubbleRef.current);
      }
      videoTrack = compositorRef.current.captureStream().getVideoTracks()[0];
    } else {
      compositorRef.current?.stop();
      compositorRef.current = null;
      videoTrack = display.getVideoTracks()[0];
    }

    if (!videoTrack) return null;

    const composed = new MediaStream(audio.track ? [videoTrack, audio.track] : [videoTrack]);
    composedRef.current = composed;
    // The monitor is video-only: playing the mixed audio back through the page
    // would feed the microphone into itself.
    setMonitor(new MediaStream([videoTrack]));
    return composed;
  }, []);

  const teardownRecorder = useCallback(async (dropTail: boolean) => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (!recorder) return;
    if (dropTail) recorder.ondataavailable = null;
    await stopRecorder(recorder);
  }, []);

  const releaseSources = useCallback(() => {
    stopTick();
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    compositorRef.current?.stop();
    compositorRef.current = null;
    audioRef.current?.close();
    audioRef.current = null;
    stopStream(displayRef.current);
    stopStream(micRef.current);
    stopStream(cameraRef.current);
    displayRef.current = null;
    micRef.current = null;
    cameraRef.current = null;
    composedRef.current = null;
    setMonitor(null);
    setCameraMonitor(null);
    setMicMonitor(null);
    setHasDisplay(false);
    setHasMic(false);
    setHasCamera(false);
    setMicMutedState(false);
  }, [stopTick]);

  // ---- sources -------------------------------------------------------------

  /**
   * "Stop sharing" from the browser's own capture bar, a mic yanked out of its
   * socket, an OS-level device change — all of them end the track. If we are
   * recording, that means "I'm done": save what we have rather than dropping
   * it on the floor.
   */
  const onSourceEnded = useCallback(() => {
    const live = recorderRef.current;
    if (live && live.state !== 'inactive') {
      void stopRef.current();
    } else {
      releaseSources();
      setPhase('idle');
    }
  }, [releaseSources]);

  const arm = useCallback(async () => {
    if (!modeSupported) return;

    // Audio-only: the microphone is the entire recording, so this is both the
    // source picker and the permission gate. A denial leaves us in `idle` with
    // an error and no way to press record, which is the point — a voice note
    // with no voice is not a degraded recording, it is an empty file.
    if (modeRef.current === 'audio') {
      setError(null);
      setPhase('arming');
      // Re-arming swaps microphones; drop the old one first so the device is
      // released rather than held open by a stream nobody reads.
      stopStream(micRef.current);
      micRef.current = null;
      setHasMic(false);
      setMicMonitor(null);
      try {
        const mic = await requestMic();
        micRef.current = mic;
        setHasMic(true);
        setMicMutedState(false);
        setMicMonitor(mic);
        mic.getAudioTracks()[0]?.addEventListener('ended', onSourceEnded);
        buildComposed();
        setPhase('ready');
      } catch (cause) {
        setError(
          cause instanceof CaptureDenied
            ? cause.message
            : 'Could not start the microphone, so there is nothing to record.',
        );
        setPhase('idle');
      }
      return;
    }

    setError(null);
    setPhase('arming');
    try {
      const display = await requestDisplay();
      displayRef.current = display;
      setHasDisplay(true);

      const [track] = display.getVideoTracks();
      track?.addEventListener('ended', onSourceEnded);

      buildComposed();
      setPhase('ready');
    } catch (cause) {
      setError(cause instanceof CaptureDenied ? cause.message : 'Could not start screen capture.');
      setPhase('idle');
    }
  }, [buildComposed, modeSupported, onSourceEnded]);

  const toggleMic = useCallback(async () => {
    if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
    // In audio-only mode the mic is not a toggle — it is the source, and
    // `arm()` owns it. Turning it off here would leave nothing to record.
    if (modeRef.current === 'audio') {
      await arm();
      return;
    }
    setError(null);
    if (micRef.current) {
      stopStream(micRef.current);
      micRef.current = null;
      setHasMic(false);
      setMicMutedState(false);
      setMicMonitor(null);
    } else {
      try {
        const mic = await requestMic();
        micRef.current = mic;
        setHasMic(true);
        setMicMonitor(mic);
      } catch (cause) {
        setError(cause instanceof CaptureDenied ? cause.message : 'Could not start the microphone.');
        return;
      }
    }
    if (displayRef.current) buildComposed();
  }, [arm, buildComposed, phase]);

  const toggleCamera = useCallback(async () => {
    if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
    // A webcam bubble needs a frame to sit in. There isn't one.
    if (modeRef.current === 'audio') return;
    setError(null);
    if (cameraRef.current) {
      stopStream(cameraRef.current);
      cameraRef.current = null;
      setHasCamera(false);
      setCameraMonitor(null);
    } else {
      if (!support.canvasCapture) {
        setError('This browser cannot composite a webcam bubble. Record without it, or use Chrome.');
        return;
      }
      try {
        const stream = await requestCamera();
        cameraRef.current = stream;
        setHasCamera(true);
        setCameraMonitor(stream);
      } catch (cause) {
        setError(cause instanceof CaptureDenied ? cause.message : 'Could not start the camera.');
        return;
      }
    }
    if (displayRef.current) buildComposed();
  }, [buildComposed, phase, support.canvasCapture]);

  const setMicMuted = useCallback((muted: boolean) => {
    // Works mid-recording: a disabled track feeds silence into the mix rather
    // than disappearing, so the audio stays continuous.
    for (const track of micRef.current?.getAudioTracks() ?? []) track.enabled = !muted;
    setMicMutedState(muted);
  }, []);

  const setBubble = useCallback((patch: Partial<CompositorSettings>) => {
    const next = { ...bubbleRef.current, ...patch };
    bubbleRef.current = next;
    compositorRef.current?.update(next);
    setBubbleState(next);
  }, []);

  const moveBubble = useCallback(
    (corner: BubbleCorner) => setBubble({ corner }),
    [setBubble],
  );

  // ---- recording -----------------------------------------------------------

  const beginRecording = useCallback(() => {
    const audioOnly = modeRef.current === 'audio';
    const composed = composedRef.current ?? buildComposed();
    if (!composed || !codec) {
      setError(
        audioOnly
          ? 'The recorder could not start. Choose your microphone again.'
          : 'The recorder could not start. Try picking your screen again.',
      );
      setPhase('ready');
      return;
    }

    chunksRef.current = [];
    setResult(null);
    clearPreview();
    resetClock();

    const uploader = new StreamingUpload(optionsRef.current.transport, setUpload);
    uploaderRef.current = uploader;
    // Not awaited: recording starts now, the session opens alongside it, and
    // chunks buffer in memory for the fraction of a second in between.
    void uploader
      .start({
        contentType: codec.contentType,
        title: optionsRef.current.makeTitle(),
        partCount: 3,
        ...(optionsRef.current.workspace ? { workspace: optionsRef.current.workspace } : {}),
        ...(optionsRef.current.orgId ? { orgId: optionsRef.current.orgId } : {}),
      })
      .catch(() => {
        // Surfaced through the upload state; the take itself is unaffected and
        // can still be downloaded from the preview screen.
      });

    // Speech at 96kbps Opus is transparent; asking for a video bitrate on a
    // stream with no video track makes some encoders unhappy, so don't.
    const recorderOptions: MediaRecorderOptions = audioOnly
      ? { audioBitsPerSecond: AUDIO_ONLY_BITRATE }
      : { videoBitsPerSecond: config.recorderBitrate, audioBitsPerSecond: SCREEN_AUDIO_BITRATE };
    if (codec.mimeType) recorderOptions.mimeType = codec.mimeType;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(composed, recorderOptions);
    } catch {
      try {
        recorder = new MediaRecorder(composed);
      } catch {
        setError('This browser refused to start a recording. Try Chrome, Edge, or Firefox.');
        setPhase('ready');
        return;
      }
    }

    recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;
      chunksRef.current.push(event.data);
      uploaderRef.current?.push(event.data);
    };
    recorder.onerror = () => {
      setError('The recorder stopped unexpectedly. Whatever was captured up to now is kept.');
      void stopRef.current();
    };

    recorderRef.current = recorder;
    recorder.start(TIMESLICE_MS);
    startTick();
    setPhase('recording');
  }, [buildComposed, clearPreview, codec, resetClock, startTick]);

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  }, []);

  const runCountdown = useCallback(() => {
    clearCountdown();
    setPhase('countdown');
    setCountdown(COUNTDOWN_FROM);

    // The remaining count lives in a local rather than in the state updater:
    // starting a recording is a side effect, and side effects do not belong
    // inside a setState callback.
    let remaining = COUNTDOWN_FROM;
    countdownTimerRef.current = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearCountdown();
        beginRecording();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [beginRecording, clearCountdown]);

  const start = useCallback(() => {
    if (phase !== 'ready') return;
    setError(null);
    if (optionsRef.current.countdown === false) beginRecording();
    else runCountdown();
  }, [beginRecording, phase, runCountdown]);

  const skipCountdown = useCallback(() => {
    if (phase !== 'countdown') return;
    clearCountdown();
    beginRecording();
  }, [beginRecording, clearCountdown, phase]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
    settleTick();
    setPhase('paused');
  }, [settleTick]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    recorder.resume();
    startTick();
    setPhase('recording');
  }, [startTick]);

  const togglePause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'recording') pause();
    else if (recorder.state === 'paused') resume();
  }, [pause, resume]);

  /**
   * Scrap this take and immediately start another. The captured source — the
   * screen, or the microphone — is untouched, so there is no second permission
   * prompt. This is the whole point of holding the MediaStream outside the
   * MediaRecorder's lifetime, and it is identical in both modes: audio-only
   * simply has less to preserve.
   */
  const restart = useCallback(() => {
    if (phase !== 'recording' && phase !== 'paused') return;
    void (async () => {
      clearCountdown();
      await teardownRecorder(true);
      uploaderRef.current?.abort();
      uploaderRef.current = null;
      chunksRef.current = [];
      resetClock();
      setError(null);
      beginRecording();
    })();
  }, [beginRecording, clearCountdown, phase, resetClock, teardownRecorder]);

  const stop = useCallback(async () => {
    if (phase !== 'recording' && phase !== 'paused') return;
    setPhase('finishing');
    clearCountdown();
    await teardownRecorder(false);
    settleTick();

    const durationMs = elapsedRef.current;
    const chunks = chunksRef.current;

    // Local preview first: whatever happens to the upload, the take exists and
    // can be played and downloaded from this screen.
    if (chunks.length > 0 && codec) {
      const raw = new Blob(chunks, { type: codec.contentType });
      const patched = await fixWebmDuration(raw, durationMs);
      const url = URL.createObjectURL(patched);
      previewUrlRef.current = url;
      setPreviewBlob(patched);
      setPreviewUrl(url);
    }

    const uploader = uploaderRef.current;
    if (uploader) {
      try {
        const completed = await uploader.finish({
          durationSec: Math.round(durationMs / 100) / 10,
        });
        setResult(completed);
      } catch {
        // Reported through upload.error; the preview screen offers a retry and
        // a download.
      }
    }
    setPhase('preview');
  }, [clearCountdown, codec, phase, settleTick, teardownRecorder]);

  stopRef.current = stop;

  const retryUpload = useCallback(async () => {
    const uploader = uploaderRef.current;
    if (!uploader) return;
    await uploader.retry();
    if (uploader.snapshot.error) return;
    try {
      const completed = await uploader.finish({
        durationSec: Math.round(elapsedRef.current / 100) / 10,
      });
      setResult(completed);
    } catch {
      // Still failing; the state carries the reason.
    }
  }, []);

  /** Is the mode's required source still in hand? Decides ready vs idle. */
  const stillArmed = useCallback(
    () => Boolean(modeRef.current === 'audio' ? micRef.current : displayRef.current),
    [],
  );

  /** Throw the take away *and* delete the server-side video. */
  const discard = useCallback(async () => {
    uploaderRef.current?.abort();
    uploaderRef.current = null;
    chunksRef.current = [];
    setResult(null);
    clearPreview();
    resetClock();
    setError(null);
    setPhase(stillArmed() ? 'ready' : 'idle');
  }, [clearPreview, resetClock, stillArmed]);

  /** Keep the take that was just saved; clear the screen for the next one. */
  const reset = useCallback(() => {
    chunksRef.current = [];
    uploaderRef.current = null;
    setResult(null);
    clearPreview();
    resetClock();
    setError(null);
    setPhase(stillArmed() ? 'ready' : 'idle');
  }, [clearPreview, resetClock, stillArmed]);

  // ---- lifecycle -----------------------------------------------------------

  /**
   * Switching modes throws the sources away. Screen capture and a microphone
   * are not interchangeable, and holding a screen share open while somebody
   * records a voice note would keep the browser's "sharing your screen" bar up
   * for no reason. The UI only offers the switch from idle, ready or arming,
   * so this can never interrupt a take.
   */
  useEffect(() => {
    if (armedModeRef.current === mode) return;
    armedModeRef.current = mode;
    releaseSources();
    clearPreview();
    resetClock();
    setResult(null);
    setError(null);
    setPhase(support.ok ? 'idle' : 'unsupported');
  }, [clearPreview, mode, releaseSources, resetClock, support.ok]);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        try {
          recorder.stop();
        } catch {
          // Already gone.
        }
      }
      releaseSources();
    };
  }, [releaseSources]);

  // The last preview URL outlives every explicit clear; release it on unmount.
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'recording' && phase !== 'paused' && phase !== 'finishing') return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [phase]);

  return {
    phase,
    mode,
    support,
    codec,
    error,
    elapsedMs,
    countdown,
    upload,
    result,
    shareId: result?.shareId ?? upload.shareId,
    monitor,
    cameraMonitor,
    micMonitor,
    hasDisplay,
    hasMic,
    hasCamera,
    armed: mode === 'audio' ? hasMic : hasDisplay,
    micMuted,
    bubble,
    previewUrl,
    previewBlob,
    arm,
    toggleMic,
    toggleCamera,
    setMicMuted,
    setBubble,
    moveBubble,
    start,
    skipCountdown,
    pause,
    resume,
    togglePause,
    restart,
    stop,
    discard,
    retryUpload,
    reset,
    releaseSources,
  };
}
